import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import Banner from "../components/Banner";
import { useBanner } from "../hooks/useBanner";

import {
  Bars3Icon,
  ClipboardDocumentCheckIcon,
  DocumentChartBarIcon,
  HomeIcon,
  InboxIcon,
  LinkIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import { Outlet, useLocation, useNavigate } from "react-router-dom";

import plainUserAvatar from "../imgs/icons/emptyUserAvatar.png";
import SideBar from "./SideBar.jsx";

import api from "../api.js";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { taskStatsAtom } from "../store/taskStatsAtom";
import { userAtom } from "../store/userAtom";
import { billingAtom } from "../store/billingAtom";
import { useWebsocket } from "../hooks/useWebsocket";

const navigationItems = [
  {
    name: "Dashboard",
    href: "/dashboard/home",
    icon: HomeIcon,
  },
  {
    name: "Tasks",
    href: "/dashboard/tasks",
    counter: true,
    icon: ClipboardDocumentCheckIcon,
  },
  {
    name: "Inbox",
    href: "/dashboard/ai-inbox",
    icon: InboxIcon,
  },
  {
    name: "Setup",
    expand: true,
    scope: "setup",
    aggregatorMenu: true,
    icon: DocumentChartBarIcon,
    children: [
      {
        name: "Knowledge Sources",
        href: "/setup/knowledge-sources",
      },
      {
        name: "Branding",
        href: "/setup/branding",
      },
      {
        name: "Intents",
        href: "/setup/intents",
      },
      {
        name: "Test",
        href: "/setup/chatbot",
      },
      {
        name: "Deploy",
        href: "/setup/deploy",
      },
      {
        name: "Share",
        href: "/setup/share",
      },
    ],
  },
  {
    name: "Integrations",
    href: "/dashboard/integrations",
    icon: LinkIcon,
  },
];

const DashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const billingInfo = useRecoilValue(billingAtom);

  const [user, setUser] = useState({
    name: "",
    email: "",
    avatar: plainUserAvatar,
  });

  const { isVisible, message, type, showBanner, hideBanner } = useBanner();

  const setTaskStats = useSetRecoilState(taskStatsAtom);
  const setUserGlobal = useSetRecoilState(userAtom);

  // WS Counters
  const chatbot_uuid = localStorage.getItem("chatbot_uuid")
  useWebsocket(
    `${process.env.REACT_APP_WSS_BASE_URL}_counter_chatbot/${chatbot_uuid}`,
    {
      onMessage: (event) => {
        Object.entries(JSON.parse(event.data)).forEach(([key, value]) => {
          switch (key) {
            case "open_tasks":
              setTaskStats({ tasks: { open: value } });
              break;
            default:
              break;
          }
        });
      },
    }
  );

  useEffect(() => {
    if (billingInfo) {
      const totalLimit = billingInfo.subscription_limit;
      const messagesUsed = billingInfo.credits_consumed;
      const percentage = (messagesUsed / totalLimit) * 100;

      if (percentage >= 80) {
        showBanner(
          `This plan's credits have been ${percentage}% consumed. Credits consumed: ${messagesUsed}`,
          "warning"
        );
      }

      if (messagesUsed > totalLimit) {
        showBanner(
          `This plan's credits have been consumed. Please upgrade your plan Credits consumed: ${messagesUsed}`,
          "error"
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingInfo]);

  useEffect(() => {
    const cb = async () => {
      try {
        await api.get(`/api/user`).then(response => {
          if (response.data) {
            setUserGlobal(response.data);

            setUser({
              avatar: response.data.picture_url,
              name: response.data.first_name
                ? `${response.data.first_name} ${response.data.last_name}`
                : undefined,
              email: response.data.email,
            });
          }
        });

        await api
          .get("/api/task/stats", {
            params: { chatbot_uuid: chatbot_uuid },
          })
          .then(response => {
            if (response.data) {
              setTaskStats(response.data);
            }
          });
      } catch (err) {
        console.log(err);
      }
    };

    cb();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      location.pathname.endsWith("/dashboard/") ||
      location.pathname.endsWith("/dashboard")
    ) {
      navigate("/dashboard/ai-inbox");
      return;
    }

    if (
      location.pathname.endsWith("/setup/") ||
      location.pathname.endsWith("/setup")
    ) {
      navigate("/setup/knowledge-sources");
    }
  }, [location.pathname, navigate]);

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const currentPath = location.pathname;

  const navigation = navigationItems.map(item => ({
    ...item,
    current:
      item.href === currentPath ||
      (item.scope === "setup" && currentPath.startsWith("/setup")),
  }));

  return (
    <div>
      <Dialog
        open={sidebarOpen}
        onClose={setSidebarOpen}
        className="relative z-50 lg:hidden"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />

        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full border border-gray-300"
          >
            <TransitionChild>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="-m-2.5 p-2.5"
                >
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon
                    aria-hidden="true"
                    className="h-6 w-6 text-white"
                  />
                </button>
              </div>
            </TransitionChild>

            <SideBar
              navigation={navigation}
              user={user}
              currentPath={currentPath}
              onClose={() => setSidebarOpen(false)}
            />
          </DialogPanel>
        </div>
      </Dialog>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <SideBar
          navigation={navigation}
          user={user}
          currentPath={currentPath}
        />
      </div>

      <div className="lg:pl-72 bg-white">
        <div className="sticky top-0 z-40 flex h-16 lg:h-0 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon aria-hidden="true" className="size-6" />
          </button>

          <img
            src="/logo.png"
            alt="SupportMagic"
            className="h-8 w-auto absolute left-1/2 transform -translate-x-1/2 sm:hidden"
          />
        </div>

        {isVisible && (
          <Banner message={message} type={type} onClose={hideBanner} />
        )}

        <main className="bg-white">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
